import React from 'react';
import { connect } from 'react-redux';
import {
  paymentMethodsPageRequest,
  changeDatePaymentMethods,
  changeOutletPaymentMethods,
  exportPaymentMethod,
} from 'actions/reports/sales';
import moment from 'moment';
import { formValueSelector } from 'redux-form';
import TableWithChild from 'components/TableReportSales/TableWithChild';
import NoDataDisplay from 'components/NoDataDisplay/NoDataDisplay';
import LoadingSpinner from 'components/LoadingSpinner';
import { rupiahWithNegativeWithoutBracket } from 'utils/formatter';
import * as clevertapReportsPaymentMethods from 'clevertap/reports/paymentMethods';
import { csvName } from 'utils/formatterCsvName';
import { getFormattedTime, getFormattedEndDate } from 'utils/hourFilter';
import withFeatureFlags from 'hocs/withFeatureFlags';
import BodyFrame from '../BodyFrame';
import styles from './PaymentMethods.css';
import ActionBar from '../ActionBar';

const exportMenus = [
  {
    display: 'Payment Methods Per Outlet',
    key: 'perOutlet',
    id: 'export-per-outlet',
  },
  {
    display: 'Payment Methods',
    key: 'paymentMethod',
  },
];

function buildRows(paymentMethods) {
  const rows = [];

  for (const method of paymentMethods) {
    const hasHeader = method.items.length > 1;

    if (hasHeader) {
      rows.push({
        parent: true,
        name: method.categoryName,
        numberOfTransactions: '',
        amount: '',
      });
    }

    for (const item of method.items) {
      rows.push({
        parent: !hasHeader,
        ...item,
      });
    }
  }
  return rows;
}

class PaymentMethods extends React.Component {
  componentDidMount() {
    this.props.paymentMethodsPageRequest();
  }

  render() {
    const {
      outlets,
      search,
      outletComparisonEnabled,
      location,
      paymentMethodsReport,
      showLoadingSpinnerReport,
    } = this.props;
    const {
      numberOfTransactions,
      totalCollected,
      paymentMethods,
    } = paymentMethodsReport;

    const rows = buildRows(paymentMethods);
    const [footBody, childBody] = this._formatDataForList(
      rows,
      numberOfTransactions,
      totalCollected
    );
    const hasMultipleOutlets = outlets && outlets.length > 1;
    const oneOutletSelected = search.outlet && search.outlet.length === 1;
    const renderTable =
      rows.length > 0 && totalCollected !== null && !showLoadingSpinnerReport;

    return (
      <div id="reports-sales-payment-methods" className={styles.wrapper}>
        <ActionBar
          onChangeDate={this._onChangeDate}
          onChangeOutlet={this._onChangeOutlet}
          onExport={this._onClickExport}
          exportMenus={
            hasMultipleOutlets && oneOutletSelected && outletComparisonEnabled
              ? exportMenus
              : []
          }
        />
        <BodyFrame location={location}>
          <div className={styles.tableWrapper}>
            <LoadingSpinner showLoadingSpinner={showLoadingSpinnerReport} />
            {renderTable && (
              <TableWithChild
                headerBody={[
                  'Payment Method',
                  'Number of Transactions',
                  'Total Collected',
                ]}
                childBody={childBody}
                footBody={footBody}
                tableClass="payment-table"
                headerBodyClassName={[
                  'header-payment-method',
                  'header-number-of-transactions',
                  'header-total-collected',
                ]}
                childBodyClassName={[
                  'body-payment-method',
                  'body-number-of-transactions',
                  'body-total-collected',
                ]}
                footBodyClassName={[
                  'foot-payment-method',
                  'foot-number-of-transactions',
                  'foot-total-collected',
                ]}
              />
            )}
            {Object.keys(paymentMethods).length === 0 &&
              !showLoadingSpinnerReport && (
                <div className="no-data-display-container">
                  <NoDataDisplay title="No Data to Display" />
                </div>
              )}
          </div>
        </BodyFrame>
      </div>
    );
  }

  _formatDataForList = (
    paymentMethods,
    numberOfTransactions,
    totalCollected
  ) => {
    const childBody = [];
    const footBody = {
      total: 'Total',
      numberOfTransactions,
      totalCollected: rupiahWithNegativeWithoutBracket(totalCollected),
    };
    paymentMethods.map(function(paymentMethod) {
      childBody.push({
        data: {
          paymentMethod: paymentMethod.name,
          numberOfTransactions: paymentMethod.numberOfTransactions,
          amount: rupiahWithNegativeWithoutBracket(paymentMethod.amount),
        },
        isParent: paymentMethod.parent,
      });
    });
    return [footBody, childBody];
  };

  _onChangeOutlet = (outletIds = []) => {
    const { search, changeOutletPaymentMethods } = this.props;
    const { startDate, endDate, startHour, endHour, allDay } = search.date;
    const { formattedStart, formattedEnd } = getFormattedTime(
      startDate,
      endDate,
      startHour,
      endHour,
      allDay
    );
    changeOutletPaymentMethods({
      outletIds,
      startDate: formattedStart,
      endDate: formattedEnd,
    });
  };

  _onChangeDate = ({ startDate, endDate, startHour, endHour, allDay }) => {
    const { search, changeDatePaymentMethods } = this.props;
    const { outlet } = search;
    const { formattedStart, formattedEnd } = getFormattedTime(
      startDate,
      endDate,
      startHour,
      endHour,
      allDay
    );
    changeDatePaymentMethods(formattedStart, formattedEnd, outlet);
  };

  _onClickExport = reportType => {
    const {
      search,
      exportPaymentMethod,
      outletComparisonEnabled,
      outlets,
    } = this.props;
    const { outlet, date } = search;
    const { startDate, endDate, startHour, endHour, allDay } = date;

    let outletIds = outlet;
    if (reportType === 'perOutlet') {
      outletIds = outlets && outlets.map(o => o.id);
    }

    const { formattedStart, formattedEnd } = getFormattedTime(
      startDate,
      endDate,
      startHour,
      endHour,
      allDay
    );
    const filename = csvName(
      moment(formattedStart),
      moment(formattedEnd),
      'Report Payment Methods',
      outletIds,
      outlets,
      allDay
    );
    const query = {
      startDate: formattedStart,
      endDate: getFormattedEndDate(formattedEnd),
      timeZone: '',
    };
    const queryParams = {
      compareOutlet: outletComparisonEnabled,
    };

    if (outletIds.length > 1 && outletComparisonEnabled) {
      clevertapReportsPaymentMethods.reportsPaymentMethodsPerOutletExport({
        outletIds,
        startDate: formattedStart,
        endDate: formattedEnd,
      });
    } else {
      clevertapReportsPaymentMethods.reportsPaymentMethodsExportData({
        outletIds,
        startDate: formattedStart,
        endDate: formattedEnd,
      });
    }

    exportPaymentMethod(query, filename, outletIds, queryParams);
  };
}

const selector = formValueSelector('search');

function mapStateToProps(state) {
  const {
    paymentMethods: paymentMethodsReport,
    showLoadingSpinnerReport,
  } = state.reports.paymentMethods;

  return {
    outlets: state.actionBar.outlets,
    search: {
      outlet: selector(state, 'outlet'),
      date: selector(state, 'date'),
    },
    paymentMethodsReport,
    showLoadingSpinnerReport,
  };
}

const mapDispatchToProps = {
  paymentMethodsPageRequest,
  changeDatePaymentMethods,
  changeOutletPaymentMethods,
  exportPaymentMethod,
};

function mapFlagsToProp(flags) {
  return {
    outletComparisonEnabled: flags.summaryPerOutlet,
  };
}

export default withFeatureFlags(mapFlagsToProp)(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )(PaymentMethods)
);
